import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Box, Drawer, IconButton, List, ListItemButton, ListItemIcon,
  ListItemText, Toolbar, Typography, Avatar, Menu, MenuItem, Divider, ListSubheader, Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Logout from '@mui/icons-material/Logout';
import * as Icons from '@mui/icons-material';

import { useAuth } from '@core/auth/AuthProvider.jsx';
import { useOffer } from '@core/offerContext.jsx';
import DocumentMeta from '@shared/components/DocumentMeta.jsx';
import { resolvePageMeta } from '@shared/seo/pageMeta.js';

const DRAWER_WIDTH = 260;

function resolveIcon(name) {
  const Icon = name && Icons[name];
  return Icon || Icons.Extension;
}

function inferSection(item) {
  if (item.section) return item.section;
  const path = item.path || '';
  if (path.startsWith('/dashboard')) return 'Pilotage';
  if (path.startsWith('/contacts') || path.startsWith('/crm') || path.startsWith('/quotes')) return 'Commercial';
  if (path.startsWith('/bookings') || path.startsWith('/support') || path.startsWith('/catalog')) return 'Opérations';
  if (
    path.startsWith('/automation')
    || path.startsWith('/workflows')
    || path.startsWith('/workflow-definitions')
    || path.startsWith('/admin')
    || path.startsWith('/notifications')
    || path.startsWith('/api-keys')
    || path.startsWith('/content')
    || path.startsWith('/media')
  ) {
    return 'Administration';
  }
  return 'Autres';
}

function groupMenu(menu) {
  const groups = new Map();
  menu.forEach((item) => {
    const section = inferSection(item);
    if (!groups.has(section)) groups.set(section, []);
    groups.get(section).push(item);
  });
  const sectionOrder = ['Pilotage', 'Commercial', 'Opérations', 'Administration', 'Autres'];
  return sectionOrder
    .filter((key) => groups.has(key))
    .map((key) => ({
      section: key,
      items: groups.get(key).sort((a, b) => (a.order ?? 999) - (b.order ?? 999)),
    }));
}

export default function AppShell({ menu = [] }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const { user, logout } = useAuth();
  const { client, theme } = useOffer();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const meta = resolvePageMeta(pathname);

  const groupedMenu = groupMenu(menu);

  const doLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box sx={{ width: DRAWER_WIDTH, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {theme?.logo ? (
          <Box
            component="img"
            src={theme.logo}
            alt=""
            sx={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 1 }}
          />
        ) : (
          <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: 'primary.main', opacity: 0.9 }} />
        )}
        <Typography variant="h6" noWrap fontWeight={700} letterSpacing="-0.02em">
          {client?.name || 'Starter'}
        </Typography>
      </Box>
      <Divider />
      <List sx={{ flex: 1, overflowY: 'auto' }}>
        {groupedMenu.map((group) => (
          <Box key={group.section} sx={{ mb: 1 }}>
            <ListSubheader
              disableSticky
              sx={{
                bgcolor: 'transparent',
                lineHeight: 1.8,
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'text.secondary',
              }}
            >
              {group.section}
            </ListSubheader>
            {group.items.map((item) => {
              const Icon = resolveIcon(item.icon);
              return (
                <ListItemButton
                  key={item.path}
                  component={NavLink}
                  to={item.path}
                  sx={{
                    '&.active': { bgcolor: 'action.selected', color: 'primary.main', fontWeight: 600 },
                    mx: 1,
                    borderRadius: 1.5,
                    transition: 'background-color 0.15s ease, transform 0.15s ease',
                    '&:hover': { bgcolor: 'action.hover', transform: 'translateX(1px)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
            })}
          </Box>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <DocumentMeta title={meta.title} description={meta.description} path={pathname} />
      <AppBar
        position="fixed"
        color="inherit"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(20,18,16,0.88)' : 'rgba(250,248,246,0.9)'),
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen((o) => !o)}
            sx={{ mr: 2, display: { md: 'none' }, minWidth: 44, minHeight: 44 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: '-0.02em' }}>
            {client?.name || 'Starter'}
          </Typography>
          <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ minWidth: 44, minHeight: 44, p: 0.5 }}>
            <Tooltip title="Compte">
              <Avatar sx={{ width: 36, height: 36, boxShadow: 1 }}>
                {(user?.displayName || user?.email || 'U').slice(0, 1).toUpperCase()}
              </Avatar>
            </Tooltip>
          </IconButton>
          <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            <MenuItem disabled>{user?.email}</MenuItem>
            <MenuItem
              onClick={() => {
                setAnchor(null);
                navigate('/profile');
              }}
            >
              Mon profil
            </MenuItem>
            <Divider />
            <MenuItem onClick={doLogout}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Se déconnecter
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxShadow: '0 20px 40px rgba(28,25,23,0.15)' } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
          open
        >
          <Toolbar />
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 2 },
          mt: 8,
          maxWidth: { md: 'min(var(--layout-max-width, 1200px), 100%)' },
          mx: { md: 'auto' },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
